import { conform, useForm } from '@conform-to/react'
import { getFieldsetConstraint, parse } from '@conform-to/zod'
import {
	type ActionFunction,
	type DataFunctionArgs,
	json,
} from '@remix-run/node'
import { newsletterMail } from '~/services/email/sendgrid.server.tsx'
import { useFetcher } from '@remix-run/react'
import { z } from 'zod'
import { redirectWithToast } from '~/utils/flash-session.server.ts'
import { ErrorList, Field } from '~/components/forms.tsx'
import { StatusButton } from '~/components/ui/status-button.tsx'

export const NewsletterSignupSchema = z.object({
	email: z.string().email(),
})

export const action: ActionFunction = async ({ request }: DataFunctionArgs) => {
	const formData = await request.formData()
	const submission = parse(formData, {
		schema: NewsletterSignupSchema,
		acceptMultipleErrors: () => true,
	})
	if (submission.intent !== 'submit') {
		return json({ status: 'idle', submission } as const)
	}
	if (!submission.value) {
		return json(
			{
				status: 'error',
				submission,
			} as const,
			{ status: 400 },
		)
	}
	const { email } = submission.value

	try {
		await newsletterMail({ email })
		return redirectWithToast(
			'/',
			{ title: `${email} signed up!` },
			{ status: 303 },
		) // Redirect to the homepage after successful submission
	} catch {
		// console.error('Error sending mail:', error)
		return json({ error: 'Failed to send email' }, { status: 500 })
	}
}

export function NewsletterSignupForm() {
	const newsLetterSignupFetcher = useFetcher<typeof action>()

	const [form, fields] = useForm({
		id: 'newsletter-signup',
		constraint: getFieldsetConstraint(NewsletterSignupSchema),
		lastSubmission: newsLetterSignupFetcher.data?.submission,
		onValidate({ formData }) {
			return parse(formData, { schema: NewsletterSignupSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<newsLetterSignupFetcher.Form
			method="post"
			action="/resources/newsletter-signup"
			{...form.props}
		>
			<div className="mt-4 flex items-center justify-between gap-4">
				<label htmlFor="email-address" className="sr-only">
					Email address
				</label>
				<Field
					labelProps={{ children: 'Email' }}
					inputProps={{
						...conform.input(fields.email),
						autoComplete: 'email',
					}}
					errors={fields.email.errors}
					className="mt-2 w-full"
				/>
				<StatusButton
					status={
						newsLetterSignupFetcher.state === 'submitting'
							? 'pending'
							: newsLetterSignupFetcher.data?.status ?? 'idle'
					}
					type="submit"
					disabled={newsLetterSignupFetcher.state !== 'idle'}
					className="w-1/3"
				>
					Submit
				</StatusButton>
			</div>
			<ErrorList errors={form.errors} id={form.errorId} />
		</newsLetterSignupFetcher.Form>
	)
}
